import Vue from "vue";
import { mapActions, mapState } from "vuex";

import AppMixin from "@/mixins/AppMixin";
import { AuthService } from "@/services/AuthService";
import { LoginAction } from "@/services/DTOs";
import { Person } from "@/services/DTOs/Person";
import { SnowplowService } from "@/services/SnowplowService";
import { IMicrosoftClarityUser } from "@torre-labs/microsoft-clarity";

import FindOpportunities from "@/components/find-opportunities/FindOpportunities";
import NotificationPreferences from "@/components/notification-preferences/NotificationPreferences";
import OpportunitySnackbar from "@/components/opportunity-snackbar/OpportunitySnackbar";
import SamplesDialog from "@/components/samples-dialog/SamplesDialog";

export default Vue.extend({
  components: {
    FindOpportunities,
    NotificationPreferences,
    OpportunitySnackbar,
    SamplesDialog
  },
  mixins: [AppMixin],
  created() {
    SnowplowService.page(this.$route, this);

    this.$router.afterEach((to, from) => {
      SnowplowService.page(to, this, from);
    });

    this.authenticate();
    this.$store.dispatch("services/init");
    this.$store.dispatch("opportunity/init");
  },
  mounted() {
    this.setupListeners();
    this.setupOpenIdMonitor();
    window.addEventListener("message", this.handlePostedMessageEvents);
  },
  beforeDestroy() {
    if (this.unsubscribeFromStore) {
      this.unsubscribeFromStore();
    }
    window.removeEventListener("message", this.handlePostedMessageEvents);
    this.removeOpenIdMonitor();
  },
  data() {
    return {
      unsubscribeFromStore: undefined as undefined | (() => void),
      openIdMonitorId: undefined as number | undefined
    };
  },
  computed: {
    authentication: () => ({ user: {}, id: "", ready: false }),
    user(): Person | undefined {
      return this.authentication.ready ? (this.authentication.user as Person) : undefined;
    },
    silentLoginIframeUrl(): string {
      return "/silent-login";
    },
    starrgateUrl(): string {
      return `${process.env.VUE_APP_DISCOVERY_STARRGATE_URL}`;
    },
    starrgatecliendId(): string {
      return `${process.env.VUE_APP_DISCOVERY_STARRGATE_CLIENT_ID}`;
    },
    routeHash(): string {
      return this.$route.hash;
    },
    ...mapState("users", ["authentication"]),
    mcUser(): IMicrosoftClarityUser {
      return {
        username: this.user && this.user.username,
        email: this.user && this.user.email,
        ggId: this.user && this.user.ggId,
        projectName: "discovery"
      };
    }
  },
  watch: {
    "authentication.ready"(): void {
      if (this.user?.locale) {
        this.updateLocale(this.user.locale);
      }
    }
  },
  methods: {
    // Temporal fix until https://github.com/vuejs/vuex/pull/1121/files is approved.
    // tslint:disable-next-line no-empty
    authenticate: () => {},
    redirectLoginRequest(): void {
      const next = this.$router.resolve({ name: this.$route.name, params: this.$route.params, query: this.$route.query });
      const routeNameToIntentDictionary: { [key: string]: (string | Array<string | undefined>) } = {
        "jobs": [undefined, "appbar:sign-in:jobs"],
        "people-name": [undefined, "appbar:sign-in:people-name"],
        "people-skill": [undefined, "appbar:sign-in:people-skill"],
        "post": [undefined, "job-post-form:sign-in"],
        "onboarding": [undefined, "job-post"],
        "details:posting": [undefined, "job-post:sign-in-job-post"],
        "details:posting-legacy": [undefined, "job-post:sign-in-job-post"],
        "details:candidates-ranking": [undefined, "job-post:sign-in-candidates-ranking"],
        "details:dashboard": [undefined, "job-post:sign-in-dashboard"],
        "details:match-and-rank": [undefined, "job-post:sign-in-match-and-rank"],
        "pipeline": [undefined, "job-post-manager:sign-in-pipeline"],
        "details:questions": [undefined, "job-post-manager:sign-in-questions"],
        "membersList": [undefined, "job-post-manager:claim-membership"]
      };
      const intent = routeNameToIntentDictionary[this.$route.name as string] || [undefined, this.$route.name as string];
      AuthService.login(new LoginAction("redirect", [next.href]), intent);
    },
    handlePostedMessageEvents(event: MessageEvent) {
      // messages posted from the check-session-iframe
      if (event.origin === this.starrgateUrl) {
        if (event.data === "changed") {
          // there was a change that needs to be synced with the current user
          if (!document.querySelector("#silent-login-iframe")) {
            this.addSilentLoginIframe();
          }
        }
        return;
      }

      // messages posted from the silent-login-iframe
      if (event.origin === window.origin && (event.data === "isAuthenticated" || event.data === "isNotAuthenticated")) {
        if (event.data === "isAuthenticated") {
          this.authenticate();
        }
        if (event.data === "isNotAuthenticated") {
          if (this.user) {
            AuthService.logout();
          }
        }

        // remove after load since no additional messages are posted from silent-login-iframe
        this.removeSilentLoginIframe();
      }
    },
    setupOpenIdMonitor() {
      if (this.openIdMonitorId) {
        window.clearInterval(this.openIdMonitorId);
      }
      this.openIdMonitorId = window.setInterval(this.postMessageToOpenIdCheckSession, 5000);
    },
    removeOpenIdMonitor() {
      if (this.openIdMonitorId) {
        window.clearInterval(this.openIdMonitorId);
      }
    },
    redirectLogoutRequest(): void {
      AuthService.logout();
    },
    setupListeners(): void {
      this.$root.$on("login-requested", this.redirectLoginRequest);
      this.$root.$on("logout-requested", this.redirectLogoutRequest);
      this.$root.$on("tdl-app-bar--brand-logo-click", () => {
        this.$router.push({ name: "home" });
      });
      this.$root.$on("reload", (locale?: string) => {
        if (locale) {
          this.updateLocale(locale);
        }
      });
    },
    addSilentLoginIframe(): void {
      const silentLoginIframe = document.createElement("iframe");
      silentLoginIframe.setAttribute("id", "silent-login-iframe");
      silentLoginIframe.setAttribute("frameborder", "0");
      silentLoginIframe.setAttribute("width", "0");
      silentLoginIframe.setAttribute("height", "0");
      silentLoginIframe.setAttribute("src", this.silentLoginIframeUrl);

      document.body.appendChild(silentLoginIframe);
    },
    postMessageToOpenIdCheckSession(): void {
      const clientId = this.starrgatecliendId;
      const iframe = document.getElementById("op-iframe") as HTMLIFrameElement;

      if (iframe && iframe.contentWindow) {
        const sessionState = AuthService.retrieveSessionState();
        const data = `${clientId} ${sessionState}`;
        iframe.contentWindow.postMessage(data, this.starrgateUrl);
      } else {
        // tslint:disable-next-line:no-console
        console.error("OpenId check session iframe not found");
      }
    },
    removeSilentLoginIframe(): void {
      if (document.querySelector("#silent-login-iframe")) {
        document.body.removeChild(document.querySelector("#silent-login-iframe")!);
      }
    },
    updateLocale(locale: string): void {
      this.$i18n.locale = locale;
      const html = document.documentElement;
      html.setAttribute("lang", locale);
    },
    ...mapActions("users", ["authenticate"])
  }
});
